import React, { ChangeEvent, FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, IconButton, Switch, Typography } from '@material-ui/core';
import { useFormikContext, useField } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import moment from 'moment';

import { ShareType } from 'api';
import { DatePickers, PaddedPaper } from 'components';
import { IShare } from 'features/sharing/types';

const useStyles = makeStyles(theme => ({
  accessIcon: {
    fontSize: theme.typography.pxToRem(24),
    marginRight: 12,
    verticalAlign: 'middle',
  },
  error: {
    color: theme.palette.error.main,
  },
  title: {
    position: 'relative',
    top: 2,
  },
}));

const Scheduler: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userScheduled, setUserScheduled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<number>();
  const { errors, setFieldValue } = useFormikContext<IShare>();
  const [shareTypeField] = useField('share_type');

  const isScheduled =
    shareTypeField.value === ShareType.Scheduled || shareTypeField.value === ShareType.Auto;

  function getNextShareType(scheduled: boolean) {
    if (userScheduled && scheduled) {
      return ShareType.Scheduled;
    }

    return scheduled ? ShareType.Auto : ShareType.Now;
  }

  function handleOpen(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleScheduleChange(event: ChangeEvent<HTMLInputElement>, scheduled: boolean) {
    const shareType = getNextShareType(scheduled);

    setFieldValue('share_type', shareType);

    if (scheduled && userScheduled) {
      setFieldValue('share_type', ShareType.Scheduled);
      setFieldValue('share_at', scheduledTime);
      setScheduledTime(scheduledTime);
      handleClose();
    }
  }

  function handleSave(time: number) {
    setFieldValue('share_type', ShareType.Scheduled);
    setFieldValue('share_at', time);

    setScheduledTime(time);
    setUserScheduled(true);
    handleClose();
  }

  function renderSecondary() {
    if (!userScheduled) {
      return (
        <Typography>
          <em>{t('sharing:scheduledNote')}</em>
        </Typography>
      );
    }

    const formattedTime = moment.unix(scheduledTime).format('dddd, MMMM Do, YYYY [at] h:mma');
    const hasError = !!errors.share_at;

    return (
      <Typography>
        {t('sharing:scheduled')}{' '}
        <span className={hasError ? classes.error : ''}>{formattedTime}</span>
      </Typography>
    );
  }

  return (
    <PaddedPaper data-testid="scheduler">
      <Grid container alignItems="baseline" justify="space-between">
        <Grid item>
          <AccessTimeIcon
            color={isScheduled ? 'primary' : 'disabled'}
            className={classes.accessIcon}
          />
        </Grid>
        <Grid item xs>
          <Grid container alignItems="center">
            <Grid item xs>
              <Typography className={classes.title} variant="subtitle2">
                {t('sharing:scheduler')}
              </Typography>
            </Grid>
            <Grid item>
              <Switch checked={isScheduled} onChange={handleScheduleChange} />
            </Grid>
          </Grid>
          {isScheduled && (
            <Grid container alignItems="center" justify="space-between">
              <Grid item>{renderSecondary()}</Grid>
              <Grid item>
                <IconButton onClick={handleOpen}>
                  <MoreHorzIcon />
                </IconButton>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <DatePickers.DateTime
        enableTime
        value={scheduledTime}
        anchorEl={anchorEl}
        onClose={handleClose}
        onSave={handleSave}
      />
    </PaddedPaper>
  );
};

export default Scheduler;
